import { Component, type ErrorInfo, type ReactNode } from "react";
import type {
  AxisChartRootArgs,
  ChainageChartRootArgs,
  MeasurementChartModel,
  MeasurementGroupChartModel,
  ProfileChartRootArgs,
  RootModule,
  ChartTypeSelectRootArgs
} from "../types";
import AxisCharts from "./AxisCharts";
import ChainageChart from "./ChainageChart";
import ChartTypeSelect from "./ChartTypeSelect";
import MeasurementChartContainer from "./MeasurementChartContainer";
import MeasurementChartGroup from "./MeasurementChartGroup";
import ProfileChart from "./ProfileChart";

type Props = {
  module: RootModule;
  args: unknown;
};

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class ChartErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false
  };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Chart rendering failed.", error, info);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="alert alert-warning m-3">
          Grafy se načetly, ale nepodařilo se je vykreslit. Obnovte stránku; pokud problém přetrvá, zkontrolujte konzoli prohlížeče.
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App({ module, args }: Props) {
  if (module === "measurement-chart") {
    const model = args as MeasurementChartModel;
    if (!model?.chartName) {
      return null;
    }

    return <MeasurementChartContainer model={model} profileId={model.profileId} />;
  }

  if (module === "measurement-chart-group") {
    const model = args as MeasurementGroupChartModel;
    if (!Array.isArray(model?.charts)) {
      return null;
    }

    return (
      <ChartErrorBoundary>
        <MeasurementChartGroup model={model} />
      </ChartErrorBoundary>
    );
  }

  if (module === "profile") {
    return <ProfileChart {...(args as ProfileChartRootArgs)} />;
  }

  if (module === "axis-charts") {
    const model = args as AxisChartRootArgs;
    if (!model?.axisId) {
      return null;
    }

    return <AxisCharts {...model} />;
  }

  if (module === "chainage-chart") {
    const model = args as ChainageChartRootArgs;
    if (!Array.isArray(model?.excavationIds)) {
      return null;
    }

    return (
      <ChainageChart
        excavationIds={model.excavationIds}
        profileId={model.profileId}
        height={model.height}
        hideHeader={model.hideHeader}
        annotation={model.annotation}
        rangeMin={model.dataXAxisMin ?? undefined}
        rangeMax={model.dataXAxisMax ?? undefined}
        xScaleUnit={model.xScaleUnit ?? undefined}
      />
    );
  }

  if (module === "chart-type-select") {
    return <ChartTypeSelect {...(args as ChartTypeSelectRootArgs)} />;
  }

  return null;
}
